/* eslint-disable max-len */
import { darken } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'wouter';
import { LFC } from '../../../Legacy';
import { Icon } from '../../../concepts/resources/Icon';
import { DropButton } from '../../../ui/DropButton';
import { HelpTip } from '../../../ui/Tip';
import { Button, ButtonProps, Colors, toRem } from './../../../ui';

// export const PrimaryHeading = styled.div`
//     font-weight: 450;
//     font-size: 1.5rem;
// `;

// export const ItemHeading = styled.div`
//     font-weight: 450;
//     font-size: 1.125rem;
// `;

/* ---------------------------- Framing & Spacing --------------------------- */

export const DefaultFrame = styled.div<{ flex?: string, unpadded?: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: ${p => p.unpadded ? '0rem' : '1rem 1.5rem'};
    ${p => !p.flex ? '' : css`
        display: flex;
        flex-direction: ${p.flex};
    `}
`;

export const FlexFrame = styled.div<{ flex?: string, indent?: any }>`
    flex: 1 1 auto;
    display: flex;
    flex-direction: ${p => p.flex ?? 'column'};
    ${p => !p.indent ? '' : css`
        padding: ${p.indent === true ? '0 1.5rem' : toRem(p.indent)};
    `}
`;

export type FlexerProps = {
    absolute?: boolean,
    align?: string,
    background?: string,
    border?: string,
    borderLeft?: string,
    borderTop?: string,
    borderRight?: string,
    borderBottom?: string,
    borderRadius?: string | number,
    boxShadow?: string,
    bottom?: string | number,
    center?: boolean | 'align' | 'justify',
    color?: string,
    disabled?: boolean,
    flex?: boolean | 'column' | 'column-reverse' | 'row' | 'row-reverse',
    gap?: string | number,
    grow?: boolean,
    height?: string | number,
    inline?: boolean,
    inset?: string | number | true,
    justify?: string,
    left?: string | number,
    margin?: string | number,
    maxHeight?: string | number,
    maxWidth?: string | number,
    minHeight?: string | number,
    minWidth?: string | number,
    opacity?: string | number,
    overflow?: string,
    padding?: string | number,
    pointerOff?: boolean,
    pointerEvents?: string,
    relative?: boolean,
    right?: string | number,
    shrink?: boolean,
    top?: string | number,
    width?: string | number,
    transform?: string,
    zIndex?: string | number,
    userSelect?: 'none' | 'auto' | 'text' | 'contain',
    textAlign?: 'left' | 'center' | 'right',
    wrap?: 'nowrap' | 'wrap' | 'wrap-reverse',
};

export const Flexer = styled.div<FlexerProps>`
    ${p => p.absolute ? 'position: absolute' : ''};
    ${p => p.relative ? 'position: relative' : ''};
    ${p => p.flex ? `display: ${p.inline ? 'inline-flex' : 'flex'}; flex-direction: ${p.flex === true ? 'column' : p.flex}` : ''};
    ${p => p.inline && !p.flex ? 'display: inline-block' : ''};
    ${p => p.grow ? 'flex: 1 1 auto;' : ''};
    ${p => p.shrink ? 'flex: 0 0 auto;' : ''};
    ${p => p.wrap === undefined ? '' : `flex-wrap: ${p.wrap};`};
    ${p => p.center === true ? 'align-items: center; justify-content: center;' : ''};
    ${p => p.center === 'align' ? 'align-items: center;' : ''};
    ${p => p.center === 'justify' ? 'justify-content: center;' : ''};
    ${p => p.align === undefined ? '' : `align-items: ${p.align};`};
    ${p => p.justify === undefined ? '' : `justify-content: ${p.justify};`};
    ${p => p.gap === undefined ? '' : `gap: ${toRem(p.gap)};`};
    ${p => p.width === undefined ? '' : `width: ${toRem(p.width)};`};
    ${p => p.height === undefined ? '' : `height: ${toRem(p.height)};`};
    ${p => p.minWidth === undefined ? '' : `min-width: ${toRem(p.minWidth)};`};
    ${p => p.minHeight === undefined ? '' : `min-height: ${toRem(p.minHeight)};`};
    ${p => p.maxWidth === undefined ? '' : `max-width: ${toRem(p.maxWidth)};`};
    ${p => p.maxHeight === undefined ? '' : `max-height: ${toRem(p.maxHeight)};`};
    ${p => p.padding === undefined ? '' : `padding: ${toRem(p.padding)};`};
    ${p => p.margin === undefined ? '' : `margin: ${toRem(p.margin)};`};
    ${p => p.zIndex === undefined ? '' : `z-index: ${p.zIndex}`};
    ${p => p.left === undefined ? '' : `left: ${toRem(p.left)}`};
    ${p => p.top === undefined ? '' : `top: ${toRem(p.top)}`};
    ${p => p.right === undefined ? '' : `right: ${toRem(p.right)}`};
    ${p => p.bottom === undefined ? '' : `bottom: ${toRem(p.bottom)}`};
    ${p => !!p.inset ? `position: absolute; inset: ${toRem(p.inset === true ? 0 : p.inset)};` : ''}
    ${p => !!p.pointerOff ? 'pointer-events: none;' : ''};
    ${p => p.pointerEvents === undefined ? '' : `pointer-events: ${p.pointerEvents}`};
    ${p => p.transform === undefined ? '' : `transform: ${p.transform};`};
    ${p => p.opacity === undefined ? '' : `opacity: ${p.opacity};`};
    ${p => p.background === undefined ? '' : `background: ${p.background};`};
    ${p => p.color === undefined ? '' : `color: ${p.color};`};
    ${p => p.borderRadius === undefined ? '' : `border-radius: ${toRem(p.borderRadius)};`};
    ${p => p.border === undefined ? '' : `border: ${p.border};`};
    ${p => p.borderLeft === undefined ? '' : `border-left: ${p.borderLeft};`};
    ${p => p.borderTop === undefined ? '' : `border-top: ${p.borderTop};`};
    ${p => p.borderRight === undefined ? '' : `border-right: ${p.borderRight};`};
    ${p => p.borderBottom === undefined ? '' : `border-bottom: ${p.borderBottom};`};
    ${p => p.boxShadow === undefined ? '' : `box-shadow: ${p.boxShadow};`};
    ${p => p.overflow === undefined ? '' : `overflow: ${p.overflow};`};
    ${p => p.userSelect === undefined ? '' : `user-select: ${p.userSelect};`};
    ${p => p.textAlign === undefined ? '' : `text-align: ${p.textAlign};`};
    ${p => p.disabled !== true ? '' : `
        pointer-events: none !important;
        user-select: none !important;
        opacity: 0.5 !important;
    `};
`;

export const Offset: LFC<{ type?: string, x?: number | string, y?: number | string }> = ({ type, x, y, children }) => {
    const style = { position: 'relative' } as any;
    if (x) style.left = toRem(x);
    if (y) style.top = toRem(y);
    return React.createElement(type ?? 'span', { style }, children);
};

export const Spacer = styled.div<{ mode?: 'x' | 'y', f?: 1 | 2 | 3 | 4 | 5 | 6 }>`
    ${p => `${p.mode === 'x' ? 'width' : 'height'}: ${toRem(0.5 + (0.5 * (p.f ?? 0)))}`};
`;

export const Divide: LFC<{ f?: 1 | 2 | 3 | 4 | 5 | 6 }> = ({ f }) => <>
    <Spacer f={f} />
    <div style={{ background: Colors.border, height: '0.0625rem' }}></div>
    <Spacer f={f} />
</>;

export const Divider = styled.div<{ mode: 'horizontal' | 'vertical', margin?: string, color?: string, size?: string }>`
    flex: 1 1 auto;
    margin: ${p => toRem(p.margin ?? '0')};
    background: ${p => p.color ?? Colors.border};

    ${p => p.mode === 'horizontal' && css`
        height: ${toRem(p.size ?? '.0625')};
    `}
    ${p => p.mode === 'vertical' && css`
        width: ${toRem(p.size ?? '.0625')};
    `}

`;

// 1C50B7
export const Content = styled.div<{ flex?: string }>`
    padding: 1.5rem 0;
`;

/* --------------------------------- Title --------------------------------- */

const HeaderTitle = styled.div`
    font-size: 1.5rem;
    font-weight: 450;
    color: ${Colors.primaryText};
`;
const HeaderInfo = styled.div`
    font-size: 1rem;
    color: ${Colors.text};
`;

const HeaderRoot = styled.div<{ block?: boolean }>`

    display: flex;
    flex-direction: column;
    justify-content: center;

    ${p => !p.block ? '' : css`
        padding: 0 1.5rem;
        box-sizing: content-box;
        height: 4.5rem;
        border-bottom: 2px solid ${Colors.primaryText};
    `}

    svg {
        color: ${Colors.primaryText};
        margin: 0 .5rem;
        transform: scale(.8);
    }
`;

const HeaderCrumbsFrame = styled.div`
    & * {
        display: inline-block;
    }
`;

const HeaderCrumbItem = styled.div`
    display: inline-flex;
    align-items: baseline;
`;

type HeaderCrumbItem = readonly (readonly [string, string | null])[];
const HeaderCrumbs: LFC<{ items: HeaderCrumbItem }> = ({ items }) =>
    <HeaderCrumbsFrame>
        {items.filter(x => !!x).map(([label, url], i) =>
            <HeaderCrumbItem key={label}>
                {i > 0 && <Icon size="1" src="deqo://icons/deqo/chevron-right" />}
                {url && <Link to={url}>
                    <HeaderTitle>{label}</HeaderTitle>
                </Link>}
                {!url && <HeaderTitle>{label}</HeaderTitle>}
            </HeaderCrumbItem>
        )}
    </HeaderCrumbsFrame>;

export const Header = Object.assign(HeaderRoot, {
    Crumbs: HeaderCrumbs,
    Title: HeaderTitle,
    Info: HeaderInfo,
});

/* --------------------------------- Buttons -------------------------------- */

export type ButtonStripAlign = 'left' | 'center' | 'right';

const buttonStripAlignToJustifyContent = (value?: ButtonStripAlign) => {
    if (value === 'right') return 'flex-end';
    if (value === 'center') return 'center';
    return 'flex-start';
};

export const ButtonStrip = styled.div<{ align?: ButtonStripAlign }>`
    display: flex;
    align-items: center;
    justify-content: ${p => buttonStripAlignToJustifyContent(p.align)};

    & > * {
        margin: 0 .5rem;
    }
    & > *:first-child {
        margin-left: 0;
    }
    & > *:last-child {
        margin-right: 0;
    }

    svg {
        width: 1.5rem;
        height: 1.5rem;
    }
`;

export const LinkButton: LFC<ButtonProps & { to?: string, href?: string, target?: string, rel?: string }> = ({ to, href, target, rel, ...props }) => {
    if (props.disabled) return <Button {...props} />;
    href = to?.startsWith('http') ? to : href;
    if (!!href) return <a href={href} target={target}><Button {...props} /></a>;
    else return <Link style={{ display: 'flex' }} to={to} target={target} rel={rel}><Button {...props} /></Link>;
};

/* --------------------------------- Toggles -------------------------------- */

export const ToggleFrame = styled.div`
    display: flex;
    align-items: center;
`;

export const ToggleHelp = styled.div`
    margin-top: -.25rem;
`;

export const ToggleLabel = styled.div<{ push?: boolean }>`
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    font-size: 1rem;
    color: ${Colors.text}; 
    position: relative;
    top: -1px;
    margin: 0 .75rem;

    ${ToggleHelp} {
        margin: .1rem 0 0 0.5rem;
    }

    &:first-child {
        margin-left: 0;
    }
    
    ${p => !p.push ? '' : css`
        flex: 0 0 auto;
        margin-left: auto !important;
    `}
`;


/* ------------------------------ Content Grid ------------------------------ */

const ItemHeading = styled.div`
    font-size: 1rem;
    font-weight: 400;
    margin: .25em .5rem;
    color: ${Colors.text};

    a:hover {
        text-decoration: underline;
    }
`;

const ItemBorder = styled.div<{ bg?: string }>`
    position: relative;
    width: 20rem;
    height: 15rem;
    border: solid 0.1875rem #d2e1ec;
    padding: .5rem;
    border-radius: .125rem;
    overflow: hidden;
    background: ${p => p.bg ?? 'none'};
    background-size: cover;
    background-position: center;

    &:hover {
        box-shadow: 0 0 0 0.125rem #d2e1ec;
    }

    &:active {
        border: solid 0.1875rem ${darken(0.15, '#d2e1ec')};
        box-shadow: 0 0 0 0.125rem ${darken(0.15, '#d2e1ec')};
    }

    img {
        position: absolute;
        top: 0rem;
        left: 0rem;
        right: .5rem;
        bottom: .5rem;
        /* width: 20rem;
        height: 15rem; */
    }
`;

const ItemInfo = styled.div<{ invert?: boolean }>`
    display: flex;
    flex-direction: ${p => p.invert ? 'row-reverse' : 'row'};
    align-items: center;
    justify-content: space-between;
    margin: 0.375em 0.375em 0;
    font-size: .75rem;
    color: ${Colors.text};
    opacity: 0.5;

    a:hover {
        text-decoration: underline;
    }
`;

const ItemButton = styled.div`
    position: absolute;
    top: 0.3rem;
    right: -0.3rem;
    color: ${Colors.text} !important;

    ${DropButton} {
        min-height: 1rem;
        padding: 0 .5rem;
        color: ${Colors.text} !important;
    }
`;

const Item = styled.div`
    position: relative;
    display: inline-block;
    vertical-align: bottom;
    margin: 1rem;    
    width: 20rem;

    &:hover {
        ${ItemInfo} {
            opacity: 1;
        }
    }
    
`;

const ContentGridBase = styled.div<{ layout?: [number, number, number] }>`
    margin: 0 -1rem;
    
    ${p => !p.layout ? '' : css`

        margin: 0 -${p.layout[2]}rem;

        ${Item} {
            width: ${p.layout[0]}rem;
            margin: ${p.layout[2]}rem;
        }

        ${ItemBorder} {
            width: 100%;
            height: ${p.layout[1]}rem;
        }

    `}
`;

export const ContentGrid = Object.assign(ContentGridBase, {
    Item,
    ItemHeading,
    ItemBorder,
    ItemInfo,
    ItemButton,
});

/* -------------------------------- Tab Menu -------------------------------- */

const TabMenuItem = styled.div<{ selected?: boolean }>`
    position: relative;
    padding: .75rem;
    font-size: 1.125rem;
    color: ${Colors.text};
    cursor: pointer;

    &:hover {
        background: ${Colors.hover};
    }
    &:active {
        background: ${Colors.active};
    }

    ${p => !p.selected ? '' : css`
        color: ${Colors.primary};
        &:before {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: ${Colors.primary};
        }
    `}
`;

const TabMenuBase = styled.div`

    /* ${HeaderInfo} {
        font-size: 1rem;
        color: ${Colors.text};
    } */
    position: relative;
    display: flex;
    align-items: center;
    
    &:before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: ${Colors.primaryText};
    }
`;

export const TabMenu = Object.assign(TabMenuBase, {
    Item: TabMenuItem,
});

/* ----------------------------- Loading & Crash ---------------------------- */

export const Loading = () =>
    <div></div>;

/* ----------------------------- Copy ---------------------------- */

const titleSizes = {
    '2xs': 0.75,
    'xs': 0.875,
    'sm': 1,
    'md': 1.125,
    'lg': 1.25,
    'xl': 1.375,
    '2xl': 1.5,
    '3xl': 1.625,
};

export type TitleProps = {
    size?: keyof typeof titleSizes,
    color?: string,
    weight?: string | number,
    flush?: boolean,
    uppercase?: boolean,
    whiteSpace?: 'initial' | 'nowrap',
    truncate?: boolean,
};
export const Title = styled.div<TitleProps>`
    margin-bottom: ${p => p.flush ? 'initial' : '0.5rem'};
    font-weight: ${p => p.weight ?? 450};
    font-size: ${p => titleSizes[p.size] ?? 1.125}rem;
    color: ${p => p.color ?? Colors.primaryText};
    line-height: 1em;
    text-transform: ${p => p.uppercase ? 'uppercase' : 'initial'};
    ${p => !!p.whiteSpace ? css`white-space: ${p.whiteSpace};` : ''}
    ${p => !!p.truncate ? css`white-space: nowrap; overflow: hidden; text-overflow: ellipsis;` : ''}

    a {
        &:hover {
            color: ${Colors.primary};
            text-decoration: underline;    
        }
    }
`;

export const TitleLine = styled.div<{ background?: string, flush?: boolean }>`
    position: relative;
    margin-bottom: ${p => p.flush ? 'initial' : '0.5rem'};
    text-align: center;
    &:before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        width: 100%;
        height: .0625rem;
        margin-top: .0625rem;
        background: ${Colors.border};
    }
    ${Title} {
        display: inline-block;
        position: relative;
        margin: 0;
        padding: 0 0.25rem;
        background: ${p => p.background ?? 'white'};
    }
`;

export const Label = styled.div`
    text-transform: uppercase;
    font-weight: 450;
    font-size: .75rem;
    color: ${Colors.text};
`;

export interface CopyProps {
    align?: 'left' | 'center' | 'right';
    bold?: boolean;
    bolder?: boolean;
    color?: string;
    em?: boolean;
    flush?: boolean;
    strike?: boolean;
    size?: string | number;
    whiteSpace?: 'initial' | 'nowrap';
    truncate?: boolean;
}

export const Copy = styled.div<CopyProps>`
    margin-bottom: ${p => p.flush ? 'initial' : '0.5rem'};
    color: ${p => p.color ?? Colors.text};
    font-size: ${p => toRem(p.size || 1)};
    font-style: ${p => p.em ? 'italic' : 'initial'};
    font-weight: ${p => p.bold ? '450' : (p.bolder ? '550' : 'initial')};
    text-align: ${p => p.align ?? 'inherit'};
    text-decoration: ${p => p.strike ? 'line-through' : 'initial'};
    line-height: 1.5em;
    ${p => !!p.whiteSpace ? css`white-space: ${p.whiteSpace};` : ''}
    ${p => !!p.truncate ? css`white-space: nowrap; overflow: hidden; text-overflow: ellipsis;` : ''}
    hr {
        margin: .25rem 0;
    }

    ul { 
        margin: .25rem 0;
        list-style-type: disc; 
        list-style-position: inside; 
    }
    ol { 
        margin: .25rem 0;
        list-style-type: decimal; 
        list-style-position: inside; 
    }   
    ul ul, ol ul { 
        list-style-type: circle; 
        list-style-position: inside; 
        margin-left: 1rem;
    }
    ol ol, ul ol { 
        list-style-type: lower-latin; 
        list-style-position: inside; 
        margin-left: 1rem;
    }

    li {
        margin-left: .5rem;
    }

    a {
        color: ${Colors.primary};
        text-decoration: underline;

        &:active {
            color: ${Colors.primaryDark};
        }
    }

    p {
        margin-bottom: 0.5em;
    }

    strong, b, em {
        font-weight: 500;
    }

    code {
        font-family: monospace;
    }

    ${HelpTip.Icon} {
        margin-left: .25em;
        margin-right: .25em;
    }
`;

export const BigCopy = styled.div<{ em?: boolean }>`
    font-size: 1.5rem;
    color: ${Colors.text};  
    font-weight: 450;
`;

export const CopyTextArea = styled.textarea`
    display: block;
    width: 100%;
    padding: 0;
    margin-bottom: 0.5rem;
    font-size: 1rem;
    color: ${Colors.text};  
    border: none;
    resize: none;
    background: none;
`;

export const CopyDrop = styled.div<{ inset?: string | number, rounding?: string | number }>`
    position: relative;
    &:before {
        z-index: -1;
        content: '';
        position: absolute;
        inset: ${p => toRem(p.inset ?? -.75)};
        background: ${Colors.l1};
        border-radius: ${p => toRem(p.rounding ?? 1)};
        opacity: 1;
        transition:
            opacity 200ms ease-out,
            transform 200ms cubic-bezier(0, .99, .2, 1.14);
        pointer-events: none;
    }

    textarea {
        display: block;
        width: 100%;
        padding: 0;
        margin-bottom: 0.5rem;
        font-size: 1rem;
        color: ${Colors.text};  
        border: none;
        resize: none;
        background: none;
    }
    
`;

export const Em = styled.span<{ underline?: boolean }>`
    ${p => p.underline ? 'text-decoration: underline' : 'font-weight: 450'};
`;

export const DeEm = styled.span`
    opacity: .65;
`;

export const Href = styled.span<{ dead?: boolean }>`
    color: ${p => p.dead ? Colors.negative : Colors.primary};
    text-decoration: underline;
    cursor: pointer;
`;

export const CopyTruncate = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    & * {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const TextArea = styled.textarea`
    padding: .5em;
    border: solid 1px #d2d6dc;
    border-radius: .25rem;

    &:focus {
        border: solid 1px ${Colors.primary};
        box-shadow: 0 0 0 1px ${Colors.primary};
    }
`;

export const DisableFrame = styled.div<{ disabled?: boolean }>`
    transition: opacity 120ms linear;
    ${p => !p.disabled ? '' : css`
        pointer-events: none;
        opacity: 0.5;
        filter: grayscale(50%);
    ` }
`;

export const NullComponent = () => null;

const BannerFrame = styled.div`
    position: relative;
    border-radius: .25rem;
    overflow: hidden;
`;

const BannerOuter = styled.div`
    position: absolute;
    inset: 0;
    z-index: 1;

    box-shadow: 0px 4px 8px rgba(116, 119, 150, 0.14);
    background-image: linear-gradient(64deg, #ab35e3 25%, #a62ae1 25%, #a62ae1 50%, #ab35e3 50%, #ab35e3 75%, #a62ae1 75%, #a62ae1 100%);
    background-size: 44.50px 91.25px;
`;

const BannerInner = styled.div`
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    color: white;
    padding: 2rem 2rem;

    font-weight: 450;
    font-size: 2rem;
    background: rgb(155,60,229);
    background: linear-gradient(90deg, rgba(155,60,229,1) 0%, rgba(155,60,229,0) 100%);
`;
